import { DOMAIN_PART } from '@fullstory/shared/regexes';
import { intl } from '@fullstory/intl';

export function prefixUrl(prefix: string, maybeRelUrl: string): string {
  if (maybeRelUrl.startsWith('/')) {
    return prefix + maybeRelUrl.slice(1);
  }

  try {
    // preserve abs urls, throws if relative
    return new URL(maybeRelUrl).href;
  } catch (_) {
    /* ignore */
  }

  return prefix + maybeRelUrl;
}

export enum HrefSafetyLevel {
  PossibleXSS,
  LocalPage,
  Safe,
}

// Set the HREF in the anchor element iff the URL is "safe".
// Returns HrefSafetyLevel.Safe if the URL is safe
// Returns a non-safe level (e.g., HrefSafetyLevel.PossibleXSS, HrefSafetyLevel.LocalPage) otherwise.
export function safeSetHrefElem(anchor: HTMLAnchorElement, url: string): HrefSafetyLevel {
  const safetyLevel = getHrefSafetyLevel(url);
  if (safetyLevel == HrefSafetyLevel.Safe) {
    anchor.setAttribute('href', url);
  }
  return safetyLevel;
}

// Returns HrefSafetyLevel.Safe if the URL is completely safe to include.
// Otherwise it returns a different safety level, such as
// HrefSafetyLevel.PossibleXSS or HrefSafetyLevel.LocalPage.
export function getHrefSafetyLevel(url: string, base: string = document.baseURI): HrefSafetyLevel {
  const parsed = new URL(url, base);

  // Don't allow anything that can run script.
  //
  // Note: For data urls, `parsed.protocol` is "data:" on Webkit and Blink but "http:"
  // on Firefox. So check for the "data:" prefix directly as well as in the protocol.
  if (
    parsed.protocol == 'javascript:' ||
    parsed.protocol == 'data:' ||
    (url && url.trim().indexOf('data:') == 0)
  ) {
    return HrefSafetyLevel.PossibleXSS; // Danger Will Robinson!
  }

  // Disallow clickability of local URLs in the event an attacker can craft mutative URL params to internal services.
  if (
    parsed.protocol == 'file:' ||
    parsed.host.indexOf('localhost') == 0 ||
    parsed.host.indexOf('127.0.0.1') == 0
  ) {
    return HrefSafetyLevel.LocalPage;
  }

  // Distrust unexpected protocols.
  switch (parsed.protocol) {
    case 'http:':
    case 'https:':
    case 'mailto:':
    case 'tel:':
      return HrefSafetyLevel.Safe;

    default:
      return HrefSafetyLevel.LocalPage;
  }
}

interface GetRelativeUrlOpts {
  preserveHash?: boolean;
  preserveOrigin?: boolean;
  preserveQuery?: boolean;
}

/**
 * [export description]
 *
 * @param   {string}              base         Base href that result should be relative to
 * @param   {string}              relative     Relative href to append to base
 * @param   {GetRelativeUrlOpts}  opts         Options to configure function behavior
 *
 * @return  {URL}                              The resulting relative URL
 */
export function createRelativeURL(base: string, relative: string, opts?: GetRelativeUrlOpts): URL {
  const baseUrl = new URL(base);
  const url = new URL(relative, base);

  if (opts?.preserveHash) {
    url.hash = baseUrl.hash;
  }

  if (opts?.preserveQuery) {
    for (const [name, value] of baseUrl.searchParams) {
      url.searchParams.append(name, value);
    }
  }

  if (opts?.preserveOrigin && url.origin !== baseUrl.origin) {
    const relativeHref = url.href.replace(url.origin, '');
    return new URL(relativeHref, baseUrl.origin);
  }

  return url;
}

export function parseURLParams(query: string = location.search.slice(1)): { [s: string]: string } {
  const m: { [s: string]: string } = {};
  if (!query) {
    // Return an empty map if query is absent.
    // If we don't exit here, the upcoming query.split call will produce an array with one element:
    // the empty string, which we don't want.
    return m;
  }
  const vs = query.split('&');
  for (let i = 0; i < vs.length; i++) {
    // Need this regex to keep from stripping the last '==' from the base64 encoded token
    const pair = vs[i].split(/=(?!=)(?!$)/);
    m[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
  }
  return m;
}

export function stripDomain(url: string): string {
  const firstSlash = url.indexOf('/');
  if (firstSlash < 0) {
    return '/';
  }
  return url.substring(firstSlash);
}

// Prepends "http://" to the URL if it doesn't already have a scheme.
export function ensureUrlScheme(url: string): string {
  if (url.indexOf('//') == -1) {
    return `http://${url}`;
  }
  return url;
}

// Strips the port, if present, from the host part of a URL, returning only the domain.
export function getDomainFromHost(host: string): string {
  let domain = host;
  const colonIdx = domain.indexOf(':');
  if (colonIdx >= 0) {
    domain = domain.slice(0, colonIdx);
  }
  return domain;
}

export function getShortLinkHost(): string {
  const domain = getDomainFromHost(window.location.host);
  const re = /app\.(\w+\.)?(\w+\.)?fullstory\.com/i;

  if (domain.match(re)) {
    return domain.replace(re, '$1$2fsty.io');
  } else {
    return window.location.host;
  }
}

// Determines whether a domain is some form of "localhost". If you have a full host, use
// getDomainFromHost() on it before passing it to this function.
export function looksLikeLocalhost(domain: string): boolean {
  return domain == 'localhost' || domain == '127.0.0.1';
}

const commonMultiLevelDomains = new Map<string, string[]>([
  [
    'uk',
    ['co', 'org', 'me', 'ltd', 'plc', 'net', 'sch', 'ac', 'gov', 'mod', 'mil', 'nhs', 'police'],
  ], // United Kingdom
  ['fr', ['com', 'tm', 'asso', 'avocat', 'aeroport', 'veterinaire']], // France
  ['ca', ['ab', 'bc', 'mb', 'nb', 'nf', 'nl', 'ns', 'nt', 'nu', 'on', 'pe', 'qc', 'sk', 'yk']], // Canada
  [
    'us',
    [
      'fed',
      'kids',
      'isa',
      'nsn',
      'dni', // Special US second level domains
      'al',
      'ak',
      'az',
      'ar',
      'ca',
      'co',
      'ct',
      'de',
      'dc',
      'fl',
      'ga',
      'hi',
      'id',
      'il',
      'in',
      'ia',
      'ks',
      'ky',
      'la',
      'ma',
      'md',
      'me',
      'mi',
      'mn',
      'ms',
      'mo',
      'mt',
      'ne',
      'nv',
      'nh',
      'nj',
      'nm',
      'ny',
      'nc',
      'nd',
      'oh',
      'ok',
      'or',
      'pa',
      'ri',
      'sc',
      'sd',
      'tn',
      'tx',
      'ut',
      'vt',
      'va',
      'wa',
      'wv',
      'wi',
      'wy', // US States
      'as',
      'gu',
      'fm',
      'mh',
      'mp',
      'pr',
      'pw',
      'um',
      'vi',
    ],
  ], // US Territories
  ['es', ['com', 'nom', 'org', 'gob', 'edu']], // Spain
  ['jp', ['ac', 'ad', 'co', 'ed', 'go', 'gr', 'lg', 'ne', 'or']], // Japan
  ['ru', ['com', 'net', 'org', 'pp', 'edu', 'ac', 'gov', 'int', 'mil']], // Russia
  ['au', ['com', 'net', 'org', 'edu', 'gov', 'asn', 'id', 'csiro']], // Australia
]);

// TODO (joshharris): This could be WAY more robust... but
// at least it will catch some common ones
// US Subdomains suck: https://en.wikipedia.org/wiki/.us
// Second Level Domains are hard in general: https://en.wikipedia.org/wiki/Second-level_domain
export function isMultiLevelDomain(tld: string, sld: string) {
  if (!commonMultiLevelDomains.has(tld)) {
    return false;
  }
  const countryCodes = commonMultiLevelDomains.get(tld) || [];
  return countryCodes.includes(sld);
}

export function hostnameFromAbsoluteUrl(u: string) {
  const url = new URL(u);
  return url.hostname;
}

export function generateDomainValidationError(domain: string): string {
  if (!domain) {
    return intl.formatMessage({ defaultMessage: 'Please provide a domain.', id: 'ZbOsxf' });
  }

  if (domain.indexOf('@') >= 0) {
    return intl.formatMessage({
      defaultMessage: "Don't include the @, just the domain",
      id: 'UDP/ms',
    });
  }

  if (domain.indexOf('.') < 0) {
    return intl.formatMessage({ defaultMessage: 'At least one dot is required', id: 'xJTlyM' });
  }

  const domainParts = domain.split('.');
  for (let i = 0; i < domainParts.length; i++) {
    if (domainParts[i].indexOf('//') >= 0) {
      return intl.formatMessage({
        defaultMessage: 'Only include the domain, i.e. without leading https://',
        id: 'aWmmc0',
      });
    }
    if (!DOMAIN_PART.test(domainParts[i])) {
      return intl.formatMessage({
        defaultMessage:
          "Domain can only contain characters a-z | A-Z | 0-9 and period '.' and dash '-'. The domain name part should not start or end with dash '-' (e.g. -google-.com). The domain name part should be between 1 and 63 characters long",
        id: 'IA7y1Y',
      });
    }
  }

  return '';
}
